import React, { useState, useEffect, useMemo } from 'react';
import './AccountsModal.css';
import { useToast } from '../context/ToastContext';

function AccountsModal({ accounts, onClose, onDelete, onRefresh, onConnect, email }) {
  const { showToast } = useToast();
  const [groups, setGroups] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'ungrouped', 'groups'
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#4285f4');
  const [selectedEmails, setSelectedEmails] = useState([]);

  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  // Helper to check if an account is ungrouped (matches logic in App.js)
  const isUngrouped = (account) => {
    return !account.groupId ||
      account.groupId === null ||
      account.groupId === undefined ||
      account.groupId === '' ||
      (typeof account.groupId === 'object' && !account.groupId._id);
  };

  // Unique emails across all connected accounts (sorted, no falsy values)
  const uniqueEmails = useMemo(
    () => Array.from(new Set(accounts.map(a => a.email).filter(Boolean))).sort(),
    [accounts]
  );

  // Keep selectedEmails in sync with uniqueEmails: pre-select all on first load,
  // auto-add newly-connected emails to selection.
  useEffect(() => {
    setSelectedEmails(prev => {
      if (prev.length === 0) return uniqueEmails;
      const merged = [...prev];
      uniqueEmails.forEach(e => {
        if (!merged.includes(e)) merged.push(e);
      });
      // Drop emails that no longer exist (account removed)
      return merged.filter(e => uniqueEmails.includes(e));
    });
  }, [uniqueEmails]);

  // Email filter: account passes if it has no email at all (legacy) or is in selection
  const matchesEmailFilter = (account) => {
    if (uniqueEmails.length === 0) return true;
    if (!account.email) return true;
    return selectedEmails.includes(account.email);
  };

  const filteredByEmail = useMemo(
    () => accounts.filter(matchesEmailFilter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, selectedEmails, uniqueEmails]
  );

  // Groups also filtered by email when group has email field
  const filteredGroupsByEmail = useMemo(() => {
    return groups.filter(g => {
      if (g.isDefault) return true; // keep "Без группы" pseudo-group
      if (!g.email) return true;
      if (uniqueEmails.length === 0) return true;
      return selectedEmails.includes(g.email);
    });
  }, [groups, selectedEmails, uniqueEmails]);

  const toggleEmailFilter = (emailValue) => {
    setSelectedEmails(prev =>
      prev.includes(emailValue)
        ? prev.filter(e => e !== emailValue)
        : [...prev, emailValue]
    );
  };

  const setAllEmailsSelected = () => setSelectedEmails(uniqueEmails);
  const clearEmailSelection = () => setSelectedEmails([]);

  const allAccountsCount = filteredByEmail.length;
  const ungroupedAccountsCount = filteredByEmail.filter(isUngrouped).length;
  const activeGroupsCount = filteredGroupsByEmail.filter(g => !g.isDefault).length;

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    if (!email) {
      setGroups([]);
      return;
    }
    try {
      const response = await fetch(`/api/groups?email=${encodeURIComponent(email)}`);
      if (!response.ok) {
        setGroups([]);
        return;
      }
      const data = await response.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching groups:', error);
      setGroups([]);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;

    try {
      const response = await fetch('/api/groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newGroupName.trim(),
          color: newGroupColor,
          email: email
        }),
      });

      if (response.ok) {
        setNewGroupName('');
        setNewGroupColor('#4285f4');
        fetchGroups();
      }
    } catch (error) {
      console.error('Error creating group:', error);
      showToast('Error creating group', 'error');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group? Accounts will be moved to "Без группы".')) {
      return;
    }

    try {
      const response = await fetch(`/api/groups/${groupId}?email=${email}`, {
        method: 'DELETE',
        headers: {
          // Add Content-Type if necessary, or ensure backend handles DELETE without body correctly
          // Typically DELETE doesn't need Content-Type unless we send JSON body
        }
      });

      if (response.ok) {
        fetchGroups();
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting group:', error);
      showToast('Error deleting group', 'error');
    }
  };

  const handleMoveAccounts = async (targetGroupId) => {
    if (selectedAccounts.length === 0) return;

    try {
      const response = await fetch('/api/groups/move-accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accountIds: selectedAccounts,
          toGroupId: targetGroupId,
          email: email
        }),
      });

      if (response.ok) {
        setSelectedAccounts([]);
        onRefresh();
      }
    } catch (error) {
      console.error('Error moving accounts:', error);
      showToast('Error moving accounts', 'error');
    }
  };

  const toggleAccountSelection = (accountId) => {
    setSelectedAccounts(prev =>
      prev.includes(accountId)
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const selectAllAccounts = () => {
    const visibleIds = filteredByEmail.map(acc => acc._id);
    const allVisibleSelected = visibleIds.length > 0 &&
      visibleIds.every(id => selectedAccounts.includes(id));
    if (allVisibleSelected) {
      // Deselect only the visible ones, keep any selection outside current filter
      setSelectedAccounts(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      // Add all visible ids to selection
      setSelectedAccounts(prev => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const getGroupName = (groupId) => {
    if (!groupId) return 'Без группы';
    const group = groups.find(g => g._id === groupId);
    return group ? group.name : 'Без группы';
  };

  const getGroupColor = (groupId) => {
    if (!groupId) return '#9e9e9e';
    const group = groups.find(g => g._id === groupId);
    return group ? group.color : '#9e9e9e';
  };

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortedAccounts = () => {
    const filtered = filteredByEmail.filter(account =>
      activeTab === 'all' || (activeTab === 'ungrouped' && isUngrouped(account))
    );

    return filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'email':
          aValue = a.email || '';
          bValue = b.email || '';
          break;
        case 'website':
          aValue = a.websiteName || a.websiteUrl || '';
          bValue = b.websiteName || b.websiteUrl || '';
          break;
        case 'group':
          aValue = getGroupName(a.groupId);
          bValue = getGroupName(b.groupId);
          break;
        default: // 'name'
          aValue = a.websiteName || '';
          bValue = b.websiteName || '';
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Manage Google Analytics Accounts</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All ({allAccountsCount})
          </button>
          <button
            className={`tab-btn ${activeTab === 'ungrouped' ? 'active' : ''}`}
            onClick={() => setActiveTab('ungrouped')}
          >
            Ungrouped ({ungroupedAccountsCount})
          </button>
          <button
            className={`tab-btn ${activeTab === 'groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('groups')}
          >
            Groups ({activeGroupsCount})
          </button>
        </div>

        {uniqueEmails.length > 0 && (
          <div className="email-filter">
            <span className="email-filter-label">Filter by email:</span>
            <div className="email-filter-chips">
              {uniqueEmails.map(emailValue => {
                const active = selectedEmails.includes(emailValue);
                return (
                  <button
                    key={emailValue}
                    type="button"
                    className={`email-chip ${active ? 'active' : ''}`}
                    onClick={() => toggleEmailFilter(emailValue)}
                    title={active ? 'Click to hide accounts with this email' : 'Click to show accounts with this email'}
                  >
                    <span className="email-chip-dot" />
                    {emailValue}
                  </button>
                );
              })}
            </div>
            {uniqueEmails.length > 1 && (
              <div className="email-filter-toggle">
                {selectedEmails.length === uniqueEmails.length ? (
                  <button type="button" className="btn btn-small" onClick={clearEmailSelection}>
                    Clear
                  </button>
                ) : (
                  <button type="button" className="btn btn-small" onClick={setAllEmailsSelected}>
                    All
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab !== 'groups' && accounts.length > 0 && (
          <div className="accounts-actions">
            <div className="actions-left">
              <button className="btn btn-small" onClick={selectAllAccounts}>
                {filteredByEmail.length > 0 && filteredByEmail.every(a => selectedAccounts.includes(a._id))
                  ? 'Deselect All'
                  : 'Select All'}
              </button>
              {selectedAccounts.length > 0 && (
                <div className="bulk-actions">
                  <span className="bulk-label">Move selected:</span>
                  <select
                    className="group-select"
                    onChange={(e) => {
                      if (e.target.value) {
                        handleMoveAccounts(e.target.value === 'ungrouped' ? null : e.target.value);
                        e.target.value = '';
                      }
                    }}
                  >
                    <option value="">Choose group...</option>
                    {filteredGroupsByEmail.filter(g => !g.isDefault).map(group => (
                      <option key={group._id} value={group._id}>
                        {group.name}
                      </option>
                    ))}
                    <option value="ungrouped">Без группы</option>
                  </select>
                </div>
              )}
            </div>

            <div className="actions-right">
              <span className="sort-label">Sort by:</span>
              <select
                className="sort-select"
                value={sortConfig.key}
                onChange={(e) => handleSort(e.target.value)}
              >
                <option value="name">Name</option>
                <option value="website">Website</option>
                <option value="email">Email</option>
                {activeTab === 'all' && <option value="group">Group</option>}
              </select>
              <button
                className="sort-direction-btn"
                onClick={() => setSortConfig(prev => ({ ...prev, direction: prev.direction === 'asc' ? 'desc' : 'asc' }))}
                title={sortConfig.direction === 'asc' ? 'Ascending' : 'Descending'}
              >
                {sortConfig.direction === 'asc' ? '↓' : '↑'}
              </button>
            </div>
          </div>
        )}

        <div className="modal-body">
          {activeTab !== 'groups' ? (
            <>
              {accounts.length === 0 ? (
                <p className="no-accounts">No accounts connected</p>
              ) : getSortedAccounts().length === 0 ? (
                <p className="no-accounts">No accounts match the current email filter</p>
              ) : (
                <>
                  <div className="accounts-list">
                    {getSortedAccounts()
                      .map((account) => (
                        <div key={account._id} className="account-item">
                          <input
                            type="checkbox"
                            className="account-checkbox"
                            checked={selectedAccounts.includes(account._id)}
                            onChange={() => toggleAccountSelection(account._id)}
                          />
                          <div className="account-info">
                            <h3>{account.propertyName}</h3>
                            <p className="account-name">{account.accountName}</p>
                            {(account.websiteName || account.websiteUrl) && (
                              <p className="account-url">
                                <a
                                  href={account.websiteUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="account-url-link"
                                >
                                  {account.websiteName || account.websiteUrl}
                                </a>
                              </p>
                            )}
                            <p className="account-meta">
                              Connected: {new Date(account.createdAt).toLocaleDateString()}
                              {account.lastSynced && (
                                <> • Last synced: {new Date(account.lastSynced).toLocaleString()}</>
                              )}
                            </p>
                            {account.email && (
                              <p className="account-email">📧 {account.email}</p>
                            )}
                            <div
                              className="account-group"
                              style={{ color: getGroupColor(account.groupId) }}
                            >
                              Group: {getGroupName(account.groupId)}
                            </div>
                          </div>
                          <button
                            className="delete-btn"
                            onClick={() => onDelete(account._id)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="groups-management">
              <div className="create-group">
                <h3>Create New Group</h3>
                <div className="group-form">
                  <input
                    type="text"
                    placeholder="Group name"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    maxLength="50"
                  />
                  <input
                    type="color"
                    value={newGroupColor}
                    onChange={(e) => setNewGroupColor(e.target.value)}
                  />
                  <button
                    className="btn btn-primary"
                    onClick={handleCreateGroup}
                    disabled={!newGroupName.trim()}
                  >
                    Create
                  </button>
                </div>
              </div>

              <div className="groups-list">
                <h3>Existing Groups</h3>
                {filteredGroupsByEmail.filter(g => !g.isDefault).length === 0 ? (
                  <p>No groups created yet</p>
                ) : (
                  filteredGroupsByEmail.filter(g => !g.isDefault).map((group) => (
                    <div key={group._id} className="group-item">
                      <div className="group-info">
                        <span
                          className="group-color-dot"
                          style={{ backgroundColor: group.color }}
                        />
                        <span className="group-name">{group.name}</span>
                        <span className="group-count">({group.accountCount} accounts)</span>
                      </div>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteGroup(group._id)}
                        title="Delete group"
                      >
                        🗑️
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={onConnect}>
            + Connect Google Analytics
          </button>
          <button className="btn btn-secondary" onClick={onRefresh}>
            Refresh
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default AccountsModal;
